"use client";

import { c as _c } from "react/compiler-runtime";
import type { TournamentCompetitionView, TournamentDetailView } from "@clutch/auth/actions/tournaments";
import type { PlayerRegistrationView, TournamentEligibilityAssessment } from "@clutch/auth/actions/tournaments/registrations";
import { ArrowRight02Icon, Calendar01Icon, ChampionIcon, GlobalIcon, InformationCircleIcon, Location01Icon, MapsSquare01Icon, MatchesIcon, NoteIcon, RankingIcon, Share08Icon, UserGroupIcon } from "@hugeicons/core-free-icons";
import { HugeiconsIcon } from "@hugeicons/react";
import { format } from "date-fns";
import type { Route } from "next";
import Image from "next/image";
import Link from "next/link";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { getSportConfig } from "@/config/sports";
import { formatTournamentCapacityCount, hasTournamentCapacityLimit, isTournamentAtCapacity } from "@/lib/shared/tournament-capacity";
import { cn } from "@/lib/utils";
import { ShareTournamentDialog } from "./share-tournament-dialog";
type PublicTournamentDetailViewProps = {
  tournament: TournamentDetailView;
  playerRegistration: PlayerRegistrationView;
  playerEligibility?: TournamentEligibilityAssessment | null;
  competition?: TournamentCompetitionView | null;
};
const ACTIVE_REGISTRATION_STATUSES = new Set(["draft", "pending", "waitlisted", "approved"]);
function playerEligibilityScopeLabel(tournament: TournamentDetailView): string {
  switch (tournament.playerEligibilityScope) {
    case "state":
      return tournament.districtState ? `Open to players from ${tournament.districtState}` : "Open to players from the host state";
    case "country":
      return `Open to players from ${tournament.countryCode}`;
    default:
      return tournament.districtName ? `Open to players from ${tournament.districtName}` : "Open to players from the host district";
  }
}
function registrationStateLabel(status: string): {
  label: string;
  tone: string;
} {
  switch (status) {
    case "approved":
      return {
        label: "You're registered",
        tone: "emerald"
      };
    case "pending":
      return {
        label: "Registration pending approval",
        tone: "amber"
      };
    case "waitlisted":
      return {
        label: "You're on the waitlist",
        tone: "blue"
      };
    case "draft":
      return {
        label: "Registration in progress",
        tone: "amber"
      };
    default:
      return {
        label: "Registered",
        tone: "emerald"
      };
  }
}
export function PublicTournamentDetailViewComponent(t0) {
  const $ = _c(243);
  const {
    tournament,
    playerRegistration,
    playerEligibility: t1,
    competition: t2
  } = t0;
  const playerEligibility = t1 === undefined ? null : t1;
  const competition = t2 === undefined ? null : t2;
  let t3;
  if ($[0] !== playerRegistration) {
    t3 = !!playerRegistration && ACTIVE_REGISTRATION_STATUSES.has(playerRegistration.status);
    $[0] = playerRegistration;
    $[1] = t3;
  } else {
    t3 = $[1];
  }
  const hasActiveRegistration = t3;
  const wasRejected = playerRegistration?.status === "rejected";
  const isRegistrationOpen = tournament.availability.isRegistrationOpen;
  let t4;
  if ($[2] !== tournament.maxParticipants || $[3] !== tournament.registrationCounts.activeSlots) {
    t4 = isTournamentAtCapacity(tournament.registrationCounts.activeSlots, tournament.maxParticipants);
    $[2] = tournament.maxParticipants;
    $[3] = tournament.registrationCounts.activeSlots;
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  const isFull = t4;
  const isEligibilityBlocked = !!playerEligibility && !playerEligibility.eligible && !hasActiveRegistration && !wasRejected;
  isEligibilityBlocked ? playerEligibility?.reasonDetails?.[0]?.message ?? playerEligibility?.reasons?.[0] ?? null : null;
  let t5;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = new Set(["missing_district", "missing_sport_profile", "missing_skill_level"]);
    $[5] = t5;
  } else {
    t5 = $[5];
  }
  const DATA_MISSING_CODES = t5;
  let t6;
  if ($[6] !== isEligibilityBlocked || $[7] !== playerEligibility?.reasonDetails) {
    t6 = isEligibilityBlocked && (playerEligibility?.reasonDetails?.length ?? 0) > 0 && (playerEligibility?.reasonDetails ?? []).every(reason => DATA_MISSING_CODES.has(reason.code));
    $[6] = isEligibilityBlocked;
    $[7] = playerEligibility?.reasonDetails;
    $[8] = t6;
  } else {
    t6 = $[8];
  }
  const isProfileDataMissing = t6;
  const canRegister = isRegistrationOpen && !hasActiveRegistration && !wasRejected && (!isEligibilityBlocked || isProfileDataMissing);
  const hasSkillRestrictions = tournament.skillRestrictions.length > 0;
  let t7;
  if ($[9] !== tournament.maxParticipants || $[10] !== tournament.registrationCounts.activeSlots) {
    t7 = formatTournamentCapacityCount(tournament.registrationCounts.activeSlots, tournament.maxParticipants);
    $[9] = tournament.maxParticipants;
    $[10] = tournament.registrationCounts.activeSlots;
    $[11] = t7;
  } else {
    t7 = $[11];
  }
  const capacityCount = t7;
  const capacityUnit = hasTournamentCapacityLimit(tournament.maxParticipants) ? "slots" : "registered";
  let t8;
  if ($[12] !== capacityCount || $[13] !== tournament.maxParticipants) {
    t8 = hasTournamentCapacityLimit(tournament.maxParticipants) ? `${capacityCount} slots filled` : `${capacityCount} registered`;
    $[12] = capacityCount;
    $[13] = tournament.maxParticipants;
    $[14] = t8;
  } else {
    t8 = $[14];
  }
  const capacitySummary = t8;
  const availabilityLabel = tournament.availability.label;
  const primaryVenue = tournament.venues[0];
  let T0;
  let T1;
  let T2;
  let directionsButton;
  let registerButton;
  let shareButton;
  let sportConfig;
  let t10;
  let t11;
  let t12;
  let t13;
  let t14;
  let t15;
  let t16;
  let t17;
  let t9;
  if ($[15] !== availabilityLabel || $[16] !== canRegister || $[17] !== competition || $[18] !== hasActiveRegistration || $[19] !== isEligibilityBlocked || $[20] !== isFull || $[21] !== isProfileDataMissing || $[22] !== isRegistrationOpen || $[23] !== playerRegistration || $[24] !== primaryVenue || $[25] !== tournament || $[26] !== wasRejected) {
    sportConfig = getSportConfig(tournament.sportSlug);
    const SportIcon = sportConfig.icon;
    let t18;
    if ($[43] === Symbol.for("react.memo_cache_sentinel")) {
      t18 = <Button variant="outline" size="lg"><HugeiconsIcon icon={Share08Icon} className="mr-2 h-4 w-4" />Share</Button>;
      $[43] = t18;
    } else {
      t18 = $[43];
    }
    let t19;
    if ($[44] !== tournament) {
      t19 = <ShareTournamentDialog tournament={tournament} trigger={t18} />;
      $[44] = tournament;
      $[45] = t19;
    } else {
      t19 = $[45];
    }
    shareButton = t19;
    let t20;
    if ($[46] !== primaryVenue) {
      t20 = primaryVenue?.mapUrl ? <Button asChild={true} size="lg" variant="outline"><a href={primaryVenue.mapUrl} target="_blank" rel="noreferrer"><HugeiconsIcon icon={MapsSquare01Icon} className="mr-2 h-4 w-4" />Directions</a></Button> : null;
      $[46] = primaryVenue;
      $[47] = t20;
    } else {
      t20 = $[47];
    }
    directionsButton = t20;
    let t21;
    if ($[48] !== hasActiveRegistration || $[49] !== playerRegistration) {
      t21 = hasActiveRegistration && playerRegistration ? registrationStateLabel(playerRegistration.status) : null;
      $[48] = hasActiveRegistration;
      $[49] = playerRegistration;
      $[50] = t21;
    } else {
      t21 = $[50];
    }
    const registeredState = t21;
    const showFinalResultsCta = tournament.status === "completed" && !!competition?.standingsPublished;
    const showFixturesCta = !showFinalResultsCta && hasActiveRegistration && !!competition && competition.matches.length > 0 && (tournament.status === "fixtures_generated" || tournament.status === "in_progress" || tournament.status === "completed");
    let t22;
    if ($[51] !== availabilityLabel || $[52] !== canRegister || $[53] !== isEligibilityBlocked || $[54] !== isFull || $[55] !== isProfileDataMissing || $[56] !== isRegistrationOpen || $[57] !== registeredState || $[58] !== showFinalResultsCta || $[59] !== showFixturesCta || $[60] !== tournament.slug || $[61] !== wasRejected) {
      t22 = showFinalResultsCta ? <Button asChild={true} size="lg"><Link href={`/t/${tournament.slug}/results`}>View Final Results</Link></Button> : showFixturesCta ? <Button asChild={true} size="lg"><Link href={`/t/${tournament.slug}/fixtures` as Route}>View Fixtures</Link></Button> : wasRejected ? <div className="flex h-12 w-full items-center justify-center gap-2 rounded-xl border border-destructive/30 bg-destructive/5 font-medium text-base text-destructive">Registration rejected</div> : registeredState ? <div className={cn("flex h-12 w-full items-center justify-center gap-2 rounded-xl border font-medium text-base", registeredState.tone === "emerald" && "border-emerald-200 bg-emerald-50 text-emerald-700 dark:border-emerald-900/60 dark:bg-emerald-950/40 dark:text-emerald-300", registeredState.tone === "amber" && "border-amber-200 bg-amber-50 text-amber-700 dark:border-amber-900/60 dark:bg-amber-950/40 dark:text-amber-300", registeredState.tone === "blue" && "border-blue-200 bg-blue-50 text-blue-700 dark:border-blue-900/60 dark:bg-blue-950/40 dark:text-blue-300")}>{registeredState.label}</div> : canRegister ? <Button asChild={true} size="lg"><Link href={`/t/${tournament.slug}/register`} data-testid={isProfileDataMissing ? "complete-profile-button" : isFull ? "join-waitlist-button" : "register-now-button"}>{isProfileDataMissing ? "Complete profile to register" : isFull ? "Join Waitlist" : "Register Now"}</Link></Button> : isRegistrationOpen && isEligibilityBlocked ? <Button asChild={true} size="lg" variant="secondary"><Link href={`/t/${tournament.slug}/register`}>View eligibility issue</Link></Button> : <Button size="lg" variant="secondary" disabled={true}>{availabilityLabel}</Button>;
      $[51] = availabilityLabel;
      $[52] = canRegister;
      $[53] = isEligibilityBlocked;
      $[54] = isFull;
      $[55] = isProfileDataMissing;
      $[56] = isRegistrationOpen;
      $[57] = registeredState;
      $[58] = showFinalResultsCta;
      $[59] = showFixturesCta;
      $[60] = tournament.slug;
      $[61] = wasRejected;
      $[62] = t22;
    } else {
      t22 = $[62];
    }
    registerButton = t22;
    t17 = "space-y-6";
    T2 = Card;
    t16 = "border-border/70";
    T1 = CardHeader;
    t14 = "gap-5";
    const t23 = isRegistrationOpen ? "bg-emerald-500" : "bg-muted-foreground/50";
    let t24;
    if ($[63] !== t23) {
      t24 = cn("relative flex h-2 w-2 rounded-full", t23);
      $[63] = t23;
      $[64] = t24;
    } else {
      t24 = $[64];
    }
    let t25;
    if ($[65] !== isRegistrationOpen) {
      t25 = isRegistrationOpen && <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-400 opacity-75" />;
      $[65] = isRegistrationOpen;
      $[66] = t25;
    } else {
      t25 = $[66];
    }
    let t26;
    if ($[67] !== t24 || $[68] !== t25) {
      t26 = <span aria-hidden={true} className={t24}>{t25}</span>;
      $[67] = t24;
      $[68] = t25;
      $[69] = t26;
    } else {
      t26 = $[69];
    }
    const t27 = isRegistrationOpen ? "text-emerald-700 dark:text-emerald-400" : "text-muted-foreground";
    let t28;
    if ($[70] !== t27) {
      t28 = cn("font-medium", t27);
      $[70] = t27;
      $[71] = t28;
    } else {
      t28 = $[71];
    }
    let t29;
    if ($[72] !== availabilityLabel || $[73] !== t28) {
      t29 = <span className={t28}>{availabilityLabel}</span>;
      $[72] = availabilityLabel;
      $[73] = t28;
      $[74] = t29;
    } else {
      t29 = $[74];
    }
    let t30;
    if ($[75] !== t26 || $[76] !== t29) {
      t30 = <div className="flex items-center gap-1.5 text-sm sm:hidden">{t26}{t29}</div>;
      $[75] = t26;
      $[76] = t29;
      $[77] = t30;
    } else {
      t30 = $[77];
    }
    let t31;
    if ($[78] !== tournament.name) {
      t31 = <h1 className="font-semibold text-3xl tracking-tight md:text-4xl">{tournament.name}</h1>;
      $[78] = tournament.name;
      $[79] = t31;
    } else {
      t31 = $[79];
    }
    let t32;
    if ($[80] !== primaryVenue) {
      t32 = primaryVenue && <div className="flex items-start text-pretty text-base text-muted-foreground"><span><Link href={`/v/${primaryVenue.slug}`} target="_blank" rel="noreferrer" className="font-medium text-foreground underline-offset-4 hover:underline">{primaryVenue.name}</Link>{primaryVenue.address ? ` - ${primaryVenue.address}` : ""}</span></div>;
      $[80] = primaryVenue;
      $[81] = t32;
    } else {
      t32 = $[81];
    }
    let t33;
    if ($[82] !== t30 || $[83] !== t31 || $[84] !== t32) {
      t33 = <div className="min-w-0 space-y-2">{t30}{t31}{t32}</div>;
      $[82] = t30;
      $[83] = t31;
      $[84] = t32;
      $[85] = t33;
    } else {
      t33 = $[85];
    }
    let t34;
    if ($[86] !== directionsButton || $[87] !== shareButton) {
      t34 = <div className="hidden shrink-0 flex-wrap gap-2 sm:flex">{shareButton}{directionsButton}</div>;
      $[86] = directionsButton;
      $[87] = shareButton;
      $[88] = t34;
    } else {
      t34 = $[88];
    }
    if ($[89] !== t33 || $[90] !== t34) {
      t15 = <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between sm:gap-5">{t33}{t34}</div>;
      $[89] = t33;
      $[90] = t34;
      $[91] = t15;
    } else {
      t15 = $[91];
    }
    t13 = "flex flex-wrap items-center gap-x-4 gap-y-2 border-border/70 border-t pt-4";
    t12 = "flex items-center gap-1.5";
    t10 = sportConfig.label;
    t11 = cn("flex h-8 w-8 items-center justify-center rounded-lg border", sportConfig.colorConfig.bgLight, sportConfig.colorConfig.border);
    T0 = SportIcon;
    t9 = cn("h-4 w-4", sportConfig.colorConfig.text);
    $[15] = availabilityLabel;
    $[16] = canRegister;
    $[17] = competition;
    $[18] = hasActiveRegistration;
    $[19] = isEligibilityBlocked;
    $[20] = isFull;
    $[21] = isProfileDataMissing;
    $[22] = isRegistrationOpen;
    $[23] = playerRegistration;
    $[24] = primaryVenue;
    $[25] = tournament;
    $[26] = wasRejected;
    $[27] = T0;
    $[28] = T1;
    $[29] = T2;
    $[30] = directionsButton;
    $[31] = registerButton;
    $[32] = shareButton;
    $[33] = sportConfig;
    $[34] = t10;
    $[35] = t11;
    $[36] = t12;
    $[37] = t13;
    $[38] = t14;
    $[39] = t15;
    $[40] = t16;
    $[41] = t17;
    $[42] = t9;
  } else {
    T0 = $[27];
    T1 = $[28];
    T2 = $[29];
    directionsButton = $[30];
    registerButton = $[31];
    shareButton = $[32];
    sportConfig = $[33];
    t10 = $[34];
    t11 = $[35];
    t12 = $[36];
    t13 = $[37];
    t14 = $[38];
    t15 = $[39];
    t16 = $[40];
    t17 = $[41];
    t9 = $[42];
  }
  let t18;
  if ($[92] !== T0 || $[93] !== t9) {
    t18 = <T0 className={t9} />;
    $[92] = T0;
    $[93] = t9;
    $[94] = t18;
  } else {
    t18 = $[94];
  }
  let t19;
  if ($[95] !== t10 || $[96] !== t11 || $[97] !== t18) {
    t19 = <span title={t10} className={t11}>{t18}</span>;
    $[95] = t10;
    $[96] = t11;
    $[97] = t18;
    $[98] = t19;
  } else {
    t19 = $[98];
  }
  let t20;
  if ($[99] !== sportConfig.label) {
    t20 = <span className="font-medium text-foreground text-sm">{sportConfig.label}</span>;
    $[99] = sportConfig.label;
    $[100] = t20;
  } else {
    t20 = $[100];
  }
  let t21;
  if ($[101] !== t12 || $[102] !== t19 || $[103] !== t20) {
    t21 = <div className={t12}>{t19}{t20}</div>;
    $[101] = t12;
    $[102] = t19;
    $[103] = t20;
    $[104] = t21;
  } else {
    t21 = $[104];
  }
  let t22;
  if ($[105] === Symbol.for("react.memo_cache_sentinel")) {
    t22 = <span aria-hidden={true} className="hidden h-4 w-px bg-border/70 sm:block" />;
    $[105] = t22;
  } else {
    t22 = $[105];
  }
  let t23;
  if ($[106] === Symbol.for("react.memo_cache_sentinel")) {
    t23 = <HugeiconsIcon icon={UserGroupIcon} className="h-4 w-4 text-muted-foreground" />;
    $[106] = t23;
  } else {
    t23 = $[106];
  }
  let t24;
  if ($[107] !== capacityCount) {
    t24 = <span className="font-medium text-foreground">{capacityCount}</span>;
    $[107] = capacityCount;
    $[108] = t24;
  } else {
    t24 = $[108];
  }
  let t25;
  if ($[109] !== capacityUnit) {
    t25 = <span className="text-muted-foreground">{capacityUnit}</span>;
    $[109] = capacityUnit;
    $[110] = t25;
  } else {
    t25 = $[110];
  }
  let t26;
  if ($[111] !== t24 || $[112] !== t25) {
    t26 = <div className="flex items-center gap-1.5 text-sm">{t23}{t24}{t25}</div>;
    $[111] = t24;
    $[112] = t25;
    $[113] = t26;
  } else {
    t26 = $[113];
  }
  let t27;
  if ($[114] === Symbol.for("react.memo_cache_sentinel")) {
    t27 = <span aria-hidden={true} className="hidden h-4 w-px bg-border/70 sm:block" />;
    $[114] = t27;
  } else {
    t27 = $[114];
  }
  const t28 = isRegistrationOpen ? "bg-emerald-500" : "bg-muted-foreground/50";
  let t29;
  if ($[115] !== t28) {
    t29 = cn("relative flex h-2 w-2 rounded-full", t28);
    $[115] = t28;
    $[116] = t29;
  } else {
    t29 = $[116];
  }
  let t30;
  if ($[117] !== isRegistrationOpen) {
    t30 = isRegistrationOpen && <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-400 opacity-75" />;
    $[117] = isRegistrationOpen;
    $[118] = t30;
  } else {
    t30 = $[118];
  }
  let t31;
  if ($[119] !== t29 || $[120] !== t30) {
    t31 = <span aria-hidden={true} className={t29}>{t30}</span>;
    $[119] = t29;
    $[120] = t30;
    $[121] = t31;
  } else {
    t31 = $[121];
  }
  const t32 = isRegistrationOpen ? "text-emerald-700 dark:text-emerald-400" : "text-muted-foreground";
  let t33;
  if ($[122] !== t32) {
    t33 = cn("font-medium", t32);
    $[122] = t32;
    $[123] = t33;
  } else {
    t33 = $[123];
  }
  let t34;
  if ($[124] !== availabilityLabel || $[125] !== t33) {
    t34 = <span className={t33}>{availabilityLabel}</span>;
    $[124] = availabilityLabel;
    $[125] = t33;
    $[126] = t34;
  } else {
    t34 = $[126];
  }
  let t35;
  if ($[127] !== t31 || $[128] !== t34) {
    t35 = <div className="hidden items-center gap-1.5 text-sm sm:flex">{t31}{t34}</div>;
    $[127] = t31;
    $[128] = t34;
    $[129] = t35;
  } else {
    t35 = $[129];
  }
  let t36;
  if ($[130] !== t13 || $[131] !== t21 || $[132] !== t26 || $[133] !== t35) {
    t36 = <div className={t13}>{t21}{t22}{t26}{t27}{t35}</div>;
    $[130] = t13;
    $[131] = t21;
    $[132] = t26;
    $[133] = t35;
    $[134] = t36;
  } else {
    t36 = $[134];
  }
  let t37;
  if ($[135] !== T1 || $[136] !== t14 || $[137] !== t15 || $[138] !== t36) {
    t37 = <T1 className={t14}>{t15}{t36}</T1>;
    $[135] = T1;
    $[136] = t14;
    $[137] = t15;
    $[138] = t36;
    $[139] = t37;
  } else {
    t37 = $[139];
  }
  let t38;
  if ($[140] !== T2 || $[141] !== t16 || $[142] !== t37) {
    t38 = <T2 className={t16}>{t37}</T2>;
    $[140] = T2;
    $[141] = t16;
    $[142] = t37;
    $[143] = t38;
  } else {
    t38 = $[143];
  }
  let t39;
  if ($[144] !== directionsButton || $[145] !== shareButton) {
    t39 = <Card className="border-border/70 p-4 sm:hidden"><div className="flex gap-2 [&>*]:flex-1 [&_a]:w-full [&_button]:w-full">{shareButton}{directionsButton}</div></Card>;
    $[144] = directionsButton;
    $[145] = shareButton;
    $[146] = t39;
  } else {
    t39 = $[146];
  }
  let t40;
  if ($[147] !== tournament.coverImageUrl || $[148] !== tournament.name) {
    t40 = tournament.coverImageUrl && <Card className="gap-0 overflow-hidden border-border/70 py-0"><div className="relative h-[500px] w-full"><Image src={tournament.coverImageUrl} alt={`${tournament.name} cover`} fill={true} className="object-cover" unoptimized={true} /></div></Card>;
    $[147] = tournament.coverImageUrl;
    $[148] = tournament.name;
    $[149] = t40;
  } else {
    t40 = $[149];
  }
  let t41;
  if ($[150] !== tournament.description) {
    t41 = tournament.description && <Card><CardHeader className="flex flex-row items-center gap-2 space-y-0"><HugeiconsIcon icon={InformationCircleIcon} className="h-5 w-5 text-muted-foreground" /><CardTitle>About this Tournament</CardTitle></CardHeader><CardContent><p className="whitespace-pre-wrap text-base text-foreground leading-relaxed">{tournament.description}</p></CardContent></Card>;
    $[150] = tournament.description;
    $[151] = t41;
  } else {
    t41 = $[151];
  }
  let t42;
  if ($[152] !== tournament.rules) {
    t42 = tournament.rules && <Card><CardHeader className="flex flex-row items-center gap-2 space-y-0"><HugeiconsIcon icon={NoteIcon} className="h-5 w-5 text-muted-foreground" /><CardTitle>{"Rules & Guidelines"}</CardTitle></CardHeader><CardContent><p className="whitespace-pre-wrap text-foreground text-sm leading-relaxed">{tournament.rules}</p></CardContent></Card>;
    $[152] = tournament.rules;
    $[153] = t42;
  } else {
    t42 = $[153];
  }
  let t43;
  if ($[154] === Symbol.for("react.memo_cache_sentinel")) {
    t43 = <div className="flex items-center gap-2"><HugeiconsIcon icon={GlobalIcon} className="h-5 w-5 text-muted-foreground" /><CardTitle>Region Eligibility</CardTitle></div>;
    $[154] = t43;
  } else {
    t43 = $[154];
  }
  const t44 = tournament.playerEligibilityScope === "state" ? "State Level" : tournament.playerEligibilityScope === "country" ? "National Level" : "District Level";
  let t45;
  if ($[155] !== t44) {
    t45 = <CardHeader className="flex flex-row items-center justify-between gap-2 space-y-0">{t43}<Badge variant="secondary" className="pointer-events-none">{t44}</Badge></CardHeader>;
    $[155] = t44;
    $[156] = t45;
  } else {
    t45 = $[156];
  }
  let t46;
  if ($[157] !== tournament) {
    t46 = playerEligibilityScopeLabel(tournament);
    $[157] = tournament;
    $[158] = t46;
  } else {
    t46 = $[158];
  }
  let t47;
  if ($[159] !== t46) {
    t47 = <CardContent><h3 className="text-base text-foreground leading-relaxed">{t46}. Players outside this region cannot register.</h3></CardContent>;
    $[159] = t46;
    $[160] = t47;
  } else {
    t47 = $[160];
  }
  let t48;
  if ($[161] !== t45 || $[162] !== t47) {
    t48 = <Card>{t45}{t47}</Card>;
    $[161] = t45;
    $[162] = t47;
    $[163] = t48;
  } else {
    t48 = $[163];
  }
  let t49;
  if ($[164] !== hasSkillRestrictions || $[165] !== tournament.skillRestrictions) {
    t49 = hasSkillRestrictions && <Card><CardHeader className="flex flex-row items-center gap-2 space-y-0"><HugeiconsIcon icon={RankingIcon} className="h-5 w-5 text-muted-foreground" /><CardTitle>Skill Eligibility</CardTitle></CardHeader><CardContent className="space-y-4">{tournament.skillRestrictions.map(_temp)}</CardContent></Card>;
    $[164] = hasSkillRestrictions;
    $[165] = tournament.skillRestrictions;
    $[166] = t49;
  } else {
    t49 = $[166];
  }
  let t50;
  if ($[167] !== competition || $[168] !== tournament.slug) {
    t50 = competition && competition.matches.length > 0 && !competition.standingsPublished ? <FixturesTeaser slug={tournament.slug} competition={competition} /> : null;
    $[167] = competition;
    $[168] = tournament.slug;
    $[169] = t50;
  } else {
    t50 = $[169];
  }
  let t51;
  if ($[170] !== competition || $[171] !== tournament.slug) {
    t51 = competition?.standingsPublished ? <ResultsTeaser slug={tournament.slug} competition={competition} /> : null;
    $[170] = competition;
    $[171] = tournament.slug;
    $[172] = t51;
  } else {
    t51 = $[172];
  }
  let t52;
  if ($[173] === Symbol.for("react.memo_cache_sentinel")) {
    t52 = <HugeiconsIcon icon={Location01Icon} className="h-5 w-5 text-muted-foreground" />;
    $[173] = t52;
  } else {
    t52 = $[173];
  }
  const t53 = tournament.venues.length > 1 ? "Venues" : "Venue";
  let t54;
  if ($[174] !== t53) {
    t54 = <CardHeader className="flex flex-row items-center gap-2 space-y-0">{t52}<CardTitle>{t53}</CardTitle></CardHeader>;
    $[174] = t53;
    $[175] = t54;
  } else {
    t54 = $[175];
  }
  let t55;
  if ($[176] !== tournament.venues) {
    t55 = <CardContent>{tournament.venues.length > 0 ? <div className="space-y-4">{tournament.venues.map(_temp2)}</div> : <p className="text-muted-foreground">Venue details will be listed soon.</p>}</CardContent>;
    $[176] = tournament.venues;
    $[177] = t55;
  } else {
    t55 = $[177];
  }
  let t56;
  if ($[178] !== t54 || $[179] !== t55) {
    t56 = <Card>{t54}{t55}</Card>;
    $[178] = t54;
    $[179] = t55;
    $[180] = t56;
  } else {
    t56 = $[180];
  }
  let t57;
  if ($[181] !== t41 || $[182] !== t42 || $[183] !== t48 || $[184] !== t49 || $[185] !== t50 || $[186] !== t51 || $[187] !== t56) {
    t57 = <div className="space-y-6 md:col-span-2">{t41}{t42}{t48}{t49}{t50}{t51}{t56}</div>;
    $[181] = t41;
    $[182] = t42;
    $[183] = t48;
    $[184] = t49;
    $[185] = t50;
    $[186] = t51;
    $[187] = t56;
    $[188] = t57;
  } else {
    t57 = $[188];
  }
  let t58;
  let t59;
  if ($[189] === Symbol.for("react.memo_cache_sentinel")) {
    t58 = <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl border border-border/50 bg-muted/40 text-primary"><HugeiconsIcon icon={Calendar01Icon} className="h-5 w-5" /></div>;
    t59 = <p className="font-medium text-sm">Dates</p>;
    $[189] = t58;
    $[190] = t59;
  } else {
    t58 = $[189];
    t59 = $[190];
  }
  let t60;
  if ($[191] !== tournament.startDate) {
    t60 = format(new Date(tournament.startDate), "MMM d");
    $[191] = tournament.startDate;
    $[192] = t60;
  } else {
    t60 = $[192];
  }
  let t61;
  if ($[193] !== tournament.endDate) {
    t61 = format(new Date(tournament.endDate), "MMM d, yyyy");
    $[193] = tournament.endDate;
    $[194] = t61;
  } else {
    t61 = $[194];
  }
  let t62;
  if ($[195] !== t60 || $[196] !== t61) {
    t62 = <div className="flex items-center gap-3">{t58}<div>{t59}<p className="text-muted-foreground text-sm">{t60} –{" "}{t61}</p></div></div>;
    $[195] = t60;
    $[196] = t61;
    $[197] = t62;
  } else {
    t62 = $[197];
  }
  let t63;
  let t64;
  if ($[198] === Symbol.for("react.memo_cache_sentinel")) {
    t63 = <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl border border-border/50 bg-muted/40 text-primary"><HugeiconsIcon icon={UserGroupIcon} className="h-5 w-5" /></div>;
    t64 = <p className="font-medium text-sm">Capacity</p>;
    $[198] = t63;
    $[199] = t64;
  } else {
    t63 = $[198];
    t64 = $[199];
  }
  let t65;
  if ($[200] !== capacitySummary) {
    t65 = <div className="flex items-center gap-3">{t63}<div>{t64}<p className="text-muted-foreground text-sm">{capacitySummary}</p></div></div>;
    $[200] = capacitySummary;
    $[201] = t65;
  } else {
    t65 = $[201];
  }
  let t66;
  let t67;
  if ($[202] === Symbol.for("react.memo_cache_sentinel")) {
    t66 = <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl border border-border/50 bg-muted/40 text-primary"><HugeiconsIcon icon={ChampionIcon} className="h-5 w-5" /></div>;
    t67 = <p className="font-medium text-sm">Eligibility</p>;
    $[202] = t66;
    $[203] = t67;
  } else {
    t66 = $[202];
    t67 = $[203];
  }
  const t68 = hasSkillRestrictions ? " / skill-restricted" : "";
  let t69;
  if ($[204] !== t68 || $[205] !== tournament.entryType || $[206] !== tournament.genderEligibility) {
    t69 = <div className="flex items-center gap-3">{t66}<div>{t67}<p className="text-muted-foreground text-sm capitalize">{tournament.genderEligibility} / {tournament.entryType}{t68}</p></div></div>;
    $[204] = t68;
    $[205] = tournament.entryType;
    $[206] = tournament.genderEligibility;
    $[207] = t69;
  } else {
    t69 = $[207];
  }
  let t70;
  if ($[208] !== t62 || $[209] !== t65 || $[210] !== t69) {
    t70 = <div className="mb-6 space-y-4">{t62}{t65}{t69}</div>;
    $[208] = t62;
    $[209] = t65;
    $[210] = t69;
    $[211] = t70;
  } else {
    t70 = $[211];
  }
  let t71;
  if ($[212] === Symbol.for("react.memo_cache_sentinel")) {
    t71 = <p className="mb-1 font-medium text-sm">Registration Deadline</p>;
    $[212] = t71;
  } else {
    t71 = $[212];
  }
  let t72;
  if ($[213] !== tournament.registrationDeadline) {
    t72 = format(new Date(tournament.registrationDeadline), "MMM d, yyyy h:mm a");
    $[213] = tournament.registrationDeadline;
    $[214] = t72;
  } else {
    t72 = $[214];
  }
  let t73;
  if ($[215] !== t72) {
    t73 = <div className="mb-4">{t71}<p className="font-mono text-muted-foreground text-sm">{t72}</p></div>;
    $[215] = t72;
    $[216] = t73;
  } else {
    t73 = $[216];
  }
  let t74;
  if ($[217] !== registerButton) {
    t74 = <div className="[&>button]:h-12 [&>button]:w-full [&>button]:rounded-xl [&>button]:text-base [&_a]:h-12 [&_a]:w-full [&_a]:rounded-xl [&_a]:text-base">{registerButton}</div>;
    $[217] = registerButton;
    $[218] = t74;
  } else {
    t74 = $[218];
  }
  let t75;
  if ($[219] !== tournament.slug || $[220] !== wasRejected) {
    t75 = wasRejected && <Link href={`/t/${tournament.slug}/register`} className="mt-3 block text-center text-destructive/60 text-sm leading-relaxed underline underline-offset-2">See the reason</Link>;
    $[219] = tournament.slug;
    $[220] = wasRejected;
    $[221] = t75;
  } else {
    t75 = $[221];
  }
  let t76;
  if ($[222] !== t73 || $[223] !== t74 || $[224] !== t75) {
    t76 = <div className="border-border border-t pt-6">{t73}{t74}{t75}</div>;
    $[222] = t73;
    $[223] = t74;
    $[224] = t75;
    $[225] = t76;
  } else {
    t76 = $[225];
  }
  let t77;
  if ($[226] !== t70 || $[227] !== t76) {
    t77 = <CardContent className="p-6 pb-4">{t70}{t76}</CardContent>;
    $[226] = t70;
    $[227] = t76;
    $[228] = t77;
  } else {
    t77 = $[228];
  }
  const t78 = tournament.requiresApproval ? "Operator approval required for entry." : "Automatic approval upon entry.";
  let t79;
  if ($[229] !== t78) {
    t79 = <div className="border-border border-t bg-muted/40 px-6 py-4 text-center font-medium text-muted-foreground text-xs">{t78}</div>;
    $[229] = t78;
    $[230] = t79;
  } else {
    t79 = $[230];
  }
  let t80;
  if ($[231] !== t77 || $[232] !== t79) {
    t80 = <div className="space-y-6 md:col-span-1"><Card className="sticky top-24 gap-0 overflow-hidden py-0">{t77}{t79}</Card></div>;
    $[231] = t77;
    $[232] = t79;
    $[233] = t80;
  } else {
    t80 = $[233];
  }
  let t81;
  if ($[234] !== t57 || $[235] !== t80) {
    t81 = <div className="grid gap-6 md:grid-cols-3">{t57}{t80}</div>;
    $[234] = t57;
    $[235] = t80;
    $[236] = t81;
  } else {
    t81 = $[236];
  }
  let t82;
  if ($[237] !== t17 || $[238] !== t38 || $[239] !== t39 || $[240] !== t40 || $[241] !== t81) {
    t82 = <div className={t17}>{t38}{t39}{t40}{t81}</div>;
    $[237] = t17;
    $[238] = t38;
    $[239] = t39;
    $[240] = t40;
    $[241] = t81;
    $[242] = t82;
  } else {
    t82 = $[242];
  }
  return t82;
}
function _temp2(venue) {
  return <div key={venue.id} className="flex gap-3"><div className="min-w-0"><h3 className="font-semibold"><Link href={`/v/${venue.slug}`} target="_blank" rel="noreferrer" className="underline-offset-4 hover:underline">{venue.name}</Link></h3><p className="text-muted-foreground text-sm">{venue.address}</p>{venue.mapUrl && <a href={venue.mapUrl} target="_blank" rel="noreferrer" className="mt-1 inline-flex items-center gap-1 text-primary text-sm underline underline-offset-4"><HugeiconsIcon icon={MapsSquare01Icon} className="h-4 w-4" />Open in maps</a>}</div></div>;
}
function _temp(group) {
  return <div key={group.skillScaleSlug} className="space-y-2"><div><h3 className="font-semibold">{group.skillScaleName}</h3><p className="text-muted-foreground text-sm">Allowed {group.levelNounPlural.toLowerCase()} for this tournament</p></div><div className="flex flex-wrap gap-2">{group.levels.map(level => <Badge key={`${group.skillScaleSlug}-${level.slug}`} variant="secondary">{level.name}</Badge>)}</div></div>;
}
function FixturesTeaser(t0) {
  const $ = _c(12);
  const {
    slug,
    competition
  } = t0;
  let t1;
  if ($[0] !== competition.matches) {
    t1 = competition.matches.filter(_temp3);
    $[0] = competition.matches;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const completed = t1.length;
  const total = competition.matches.length;
  let t2;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <div className="flex items-center gap-2"><HugeiconsIcon icon={MatchesIcon} className="h-5 w-5 text-muted-foreground" /><CardTitle>{"Fixtures & Draw"}</CardTitle></div>;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const t3 = `/t/${slug}/fixtures` as Route;
  let t4;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = <HugeiconsIcon icon={ArrowRight02Icon} className="size-4" />;
    $[3] = t4;
  } else {
    t4 = $[3];
  }
  let t5;
  if ($[4] !== t3) {
    t5 = <CardHeader className="flex flex-row items-center justify-between gap-2 space-y-0">{t2}<Link href={t3} className="inline-flex items-center gap-1 font-medium text-primary text-sm hover:underline">View fixtures{t4}</Link></CardHeader>;
    $[4] = t3;
    $[5] = t5;
  } else {
    t5 = $[5];
  }
  let t6;
  if ($[6] !== completed || $[7] !== total) {
    t6 = <CardContent><p className="text-foreground text-sm">{completed} of {total} matches complete.</p></CardContent>;
    $[6] = completed;
    $[7] = total;
    $[8] = t6;
  } else {
    t6 = $[8];
  }
  let t7;
  if ($[9] !== t5 || $[10] !== t6) {
    t7 = <Card>{t5}{t6}</Card>;
    $[9] = t5;
    $[10] = t6;
    $[11] = t7;
  } else {
    t7 = $[11];
  }
  return t7;
}
function _temp3(match) {
  return ["completed", "bye", "walkover", "cancelled"].includes(match.status);
}
function ResultsTeaser(t0) {
  const $ = _c(20);
  const {
    slug,
    competition
  } = t0;
  const t1 = competition.results;
  let T0;
  let T1;
  let t2;
  let t3;
  let t4;
  let t5;
  if ($[0] !== competition.results || $[1] !== slug) {
    t5 = Symbol.for("react.early_return_sentinel");
    bb0: {
      const podium = t1.slice(0, 3);
      if (podium.length === 0) {
        t5 = null;
        break bb0;
      }
      T1 = Card;
      let t6;
      if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
        t6 = <div className="flex items-center gap-2"><HugeiconsIcon icon={ChampionIcon} className="h-5 w-5 text-muted-foreground" /><CardTitle>Final results</CardTitle></div>;
        $[8] = t6;
      } else {
        t6 = $[8];
      }
      const t7 = `/t/${slug}/results`;
      let t8;
      if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
        t8 = <HugeiconsIcon icon={ArrowRight02Icon} className="size-4" />;
        $[9] = t8;
      } else {
        t8 = $[9];
      }
      if ($[10] !== t7) {
        t4 = <CardHeader className="flex flex-row items-center justify-between gap-2 space-y-0">{t6}<Link href={t7} className="inline-flex items-center gap-1 font-medium text-primary text-sm hover:underline">See full results{t8}</Link></CardHeader>;
        $[10] = t7;
        $[11] = t4;
      } else {
        t4 = $[11];
      }
      T0 = CardContent;
      t2 = "grid items-start gap-2 sm:grid-cols-3";
      t3 = podium.map(_temp4);
    }
    $[0] = competition.results;
    $[1] = slug;
    $[2] = T0;
    $[3] = T1;
    $[4] = t2;
    $[5] = t3;
    $[6] = t4;
    $[7] = t5;
  } else {
    T0 = $[2];
    T1 = $[3];
    t2 = $[4];
    t3 = $[5];
    t4 = $[6];
    t5 = $[7];
  }
  if (t5 !== Symbol.for("react.early_return_sentinel")) {
    return t5;
  }
  let t6;
  if ($[12] !== T0 || $[13] !== t2 || $[14] !== t3) {
    t6 = <T0 className={t2}>{t3}</T0>;
    $[12] = T0;
    $[13] = t2;
    $[14] = t3;
    $[15] = t6;
  } else {
    t6 = $[15];
  }
  let t7;
  if ($[16] !== T1 || $[17] !== t4 || $[18] !== t6) {
    t7 = <T1>{t4}{t6}</T1>;
    $[16] = T1;
    $[17] = t4;
    $[18] = t6;
    $[19] = t7;
  } else {
    t7 = $[19];
  }
  return t7;
}
function _temp4(row) {
  return <div key={row.registrationId} className={cn("flex items-center justify-between gap-4 rounded-2xl border p-4 shadow-none", row.position === 1 ? "border-green-500/20 bg-green-50/50 dark:bg-green-950/20" : "bg-muted/30")}><div className="min-w-0 flex-1"><p className="mb-0.5 text-muted-foreground text-xs uppercase tracking-[0.1em]">Position {row.position}</p><p className="truncate font-heading font-semibold text-base">{row.participant?.displayName ?? row.registrationId}</p></div><div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full border bg-background"><span className="font-bold font-mono text-lg">{row.position}</span></div></div>;
}